<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { RouterLink } from 'vue-router'
import { Menu, X } from 'lucide-vue-next'
import BrandMark from './BrandMark.vue'
import MiniProgramDialog from './MiniProgramDialog.vue'
import { NOVA_AI_URL } from '@/config/external-links'
import { authSession } from '@/services/auth'

const menuOpen = ref(false)
const miniProgramOpen = ref(false)
const accountPath = computed(() => authSession.isAuthenticated.value ? '/account' : '/login')
const accountLabel = computed(() => authSession.isAuthenticated.value ? '我的账号' : '账号入口')
const closeMenu = () => { menuOpen.value = false }
const onKeydown = (event: KeyboardEvent) => {
  if (event.key === 'Escape') closeMenu()
}

watch(menuOpen, (open) => {
  document.body.style.overflow = open ? 'hidden' : ''
})

onMounted(() => window.addEventListener('keydown', onKeydown))
onBeforeUnmount(() => {
  window.removeEventListener('keydown', onKeydown)
  document.body.style.overflow = ''
})
</script>

<template>
  <header class="site-header">
    <div class="header-inner">
      <RouterLink to="/" class="brand-link" aria-label="Nova·云枢首页" @click="closeMenu">
        <BrandMark />
        <span class="brand-copy">
          <strong>Nova·云枢</strong>
          <small>中文 AI 视频创作平台</small>
        </span>
      </RouterLink>

      <button
        class="mobile-menu-button"
        type="button"
        :aria-expanded="menuOpen"
        :aria-label="menuOpen ? '关闭导航菜单' : '打开导航菜单'"
        @click="menuOpen = !menuOpen"
      >
        <X v-if="menuOpen" :size="22" aria-hidden="true" />
        <Menu v-else :size="22" aria-hidden="true" />
      </button>

      <nav class="main-nav" :class="{ open: menuOpen }" aria-label="主导航">
        <RouterLink to="/works" @click="closeMenu">发现作品</RouterLink>
        <RouterLink to="/#creators" @click="closeMenu">创作者</RouterLink>
        <RouterLink to="/tasks" @click="closeMenu">接单广场</RouterLink>
        <RouterLink to="/courses" @click="closeMenu">AIGC 课堂</RouterLink>
        <RouterLink to="/#business" @click="closeMenu">商业合作</RouterLink>
        <div class="mobile-nav-actions">
          <RouterLink :to="accountPath" @click="closeMenu">{{ accountLabel }}</RouterLink>
          <a :href="NOVA_AI_URL" target="_blank" rel="noopener" @click="closeMenu">开始创作</a>
          <button type="button" @click="closeMenu(); miniProgramOpen = true">小程序入口</button>
        </div>
      </nav>

      <div class="header-actions">
        <RouterLink class="text-action" :to="accountPath">{{ accountLabel }}</RouterLink>
        <a class="primary-action small" :href="NOVA_AI_URL" target="_blank" rel="noopener">开始创作</a>
        <button class="ghost-action small" type="button" @click="miniProgramOpen = true">小程序入口</button>
      </div>
    </div>
  </header>
  <MiniProgramDialog :open="miniProgramOpen" @close="miniProgramOpen = false" />
</template>
